import { agentReferenceMetadataToTitle } from '@/agent/references/util/agent-reference-title'
import { ClientAgentReference } from '@/agent/references/store/schemas'
import { CaseStatusCheckedLabel } from '@/citator/components/CaseStatusCheckedLabel'
import { CaseStatusTag } from '@/citator/components/CaseStatusTag'
import { isCheckedCaseStatus, isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'
import ReferenceMetadataSummary from './ReferenceMetadataSummary'

type CitationSourceTooltipContentProps = {
  reference: ClientAgentReference
  /** Footnote label ([1], [1.2]); omitted when triggered from a legal citation link. */
  citationLabel?: string
  caseStatus: string | null | undefined
  isOpening: boolean
  error: string | null
  onViewReference: () => void
  onViewCitationAnalysis: () => void
}

/**
 * The body of a citation source hover tooltip: source title (click = view source),
 * reference metadata (pages / jurisdiction / date), and the citator case-status
 * treatment (click = view citation analysis).
 *
 * One content for every citation trigger (numbered badge, legal citation link) so
 * what the user sees about a source never depends on which element they hovered.
 */
export default function CitationSourceTooltipContent(props: CitationSourceTooltipContentProps) {
  const { reference, citationLabel, caseStatus, isOpening, error, onViewReference, onViewCitationAnalysis } = props

  const title = agentReferenceMetadataToTitle(reference)

  return (
    <div className="max-w-xs xs:max-w-screen-xs sm:max-w-screen-sm py-1 leading-5 tracking-[-0.15px]">
      {citationLabel && <div className="font-semibold text-sm">[{citationLabel}]</div>}

      <button
        onClick={onViewReference}
        disabled={isOpening}
        className="block w-full text-left mt-1 compatible-word-break text-sm font-medium text-brand-700 underline hover:text-brand-800 cursor-pointer whitespace-pre-wrap disabled:cursor-wait"
      >
        {title}
      </button>

      <div className="mt-1 pr-1 text-sm font-normal text-brand-neutral-950 compatible-word-break">
        <ReferenceMetadataSummary reference={reference} />
      </div>

      {error && <div className="mt-2 text-xs font-medium text-red-800">{error}</div>}

      {isDisplayableCaseStatus(caseStatus) && (
        <div className="mt-2 flex">
          <button
            type="button"
            disabled={isOpening}
            onClick={onViewCitationAnalysis}
            className="inline-flex cursor-pointer rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-wait"
          >
            {isCheckedCaseStatus(caseStatus) ? (
              <CaseStatusCheckedLabel label="No negative or cautionary treatment detected." size="default" />
            ) : (
              <CaseStatusTag status={caseStatus} />
            )}
          </button>
        </div>
      )}
    </div>
  )
}
